import { useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send } from "lucide-react";

interface WidgetConfig {
  enabled: boolean;
  name: string;
  greeting: string;
  accentColor: string;
  position: string;
  collectName: boolean;
  collectEmail: boolean;
  collectPhone: boolean;
  attributionMode?: "drpr" | "custom" | "hidden";
  customAttribution?: string | null;
  customLogoUrl?: string | null;
}

interface ChatMessage {
  role: "user" | "assistant";
  text: string;
  ts: string;
}

export default function WidgetUI() {
  const params = new URLSearchParams(window.location.search);
  const ws = params.get("ws") || "";
  const preview = params.get("preview") === "1";

  const [cfg, setCfg] = useState<WidgetConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [convId, setConvId] = useState<number | null>(null);
  const [sending, setSending] = useState(false);
  const [leadSubmitted, setLeadSubmitted] = useState(false);
  const [visitorName, setVisitorName] = useState("");
  const [visitorEmail, setVisitorEmail] = useState("");
  const [visitorPhone, setVisitorPhone] = useState("");
  const [leadFormVisible, setLeadFormVisible] = useState(false);
  const msgsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!ws && !preview) { setLoading(false); return; }
    if (preview) {
      setCfg({ enabled: true, name: params.get('name')||'Site Agent', greeting: params.get('greeting')||'Hi! How can I help?', accentColor: params.get('color')||'#3b82f6', position: "bottom-right", collectName: params.get('cn')!=='0', collectEmail: params.get('ce')!=='0', collectPhone: params.get('cp')==='1', attributionMode: (params.get('attr')||'drpr') as 'drpr'|'custom'|'hidden', customAttribution: params.get('customAttr')||null, customLogoUrl: params.get('logoUrl')||null });
      setLoading(false);
      return;
    }
    fetch(`/api/site-agent/config?ws=${ws}`)
      .then(r => r.json())
      .then(d => { setCfg(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [ws, preview]);

  useEffect(() => {
    if (cfg?.enabled) {
      setMessages([{ role: "assistant", text: cfg.greeting, ts: new Date().toISOString() }]);
      const needsForm = cfg.collectName || cfg.collectEmail || cfg.collectPhone;
      setLeadFormVisible(needsForm);
    }
  }, [cfg]);

  useEffect(() => {
    if (msgsRef.current) msgsRef.current.scrollTop = msgsRef.current.scrollHeight;
  }, [messages, sending]);

  async function sendMessage() {
    if (!input.trim() || sending) return;
    const text = input.trim();
    setInput("");
    const newMsg: ChatMessage = { role: "user", text, ts: new Date().toISOString() };
    setMessages(prev => [...prev, newMsg]);
    setSending(true);
    try {
      const r = await fetch("/api/site-agent/chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ws,
          message: text,
          conversationId: convId,
          visitor: { name: visitorName, email: visitorEmail, phone: visitorPhone },
          pageUrl: window.location.href,
        }),
      });
      const d = await r.json();
      if (d.conversationId) setConvId(d.conversationId);
      setMessages(prev => [...prev, { role: "assistant", text: d.reply || "Sorry, I couldn't process that.", ts: new Date().toISOString() }]);
    } catch {
      setMessages(prev => [...prev, { role: "assistant", text: "Connection error. Please try again.", ts: new Date().toISOString() }]);
    } finally {
      setSending(false);
    }
  }

  function submitLeadForm(e: React.FormEvent) {
    e.preventDefault();
    setLeadSubmitted(true);
    setLeadFormVisible(false);
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-white">
        <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!cfg?.enabled && !preview) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50 text-gray-400 text-sm">
        Widget not available
      </div>
    );
  }

  const accent = cfg?.accentColor || "#3b82f6";
  const attrMode = cfg?.attributionMode || "drpr";

  function renderAttribution() {
    if (attrMode === "hidden") return null;
    if (attrMode === "custom" && cfg?.customAttribution) {
      return (
        <div className="text-center py-1.5 text-[10px] text-gray-300 bg-white shrink-0 flex items-center justify-center gap-1">
          {cfg.customLogoUrl && (
            <img
              src={cfg.customLogoUrl}
              alt="Brand logo"
              className="h-3 w-auto inline-block"
              onError={e => { (e.target as HTMLImageElement).style.display = "none"; }}
            />
          )}
          <span>{cfg.customAttribution}</span>
        </div>
      );
    }
    return (
      <div className="text-center py-1.5 text-[10px] text-gray-300 bg-white shrink-0">
        Powered by <span className="font-bold">drpr.ai</span>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen bg-white font-sans" style={{ fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" }}>
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 text-white shrink-0" style={{ background: accent }}>
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
            <MessageCircle size={16} className="text-white" />
          </div>
          <span className="font-bold text-sm">{cfg?.name || "Chat"}</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-2 h-2 rounded-full bg-green-300 animate-pulse" />
          <span className="text-xs text-white/70">Online</span>
        </div>
      </div>

      {/* Messages */}
      <div ref={msgsRef} className="flex-1 overflow-y-auto p-3 flex flex-col gap-2" style={{ background: "#fafafa" }}>
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div
              className="max-w-[80%] text-sm rounded-2xl px-3 py-2 leading-relaxed"
              style={m.role === "user"
                ? { background: accent, color: "#fff", borderBottomRightRadius: "4px" }
                : { background: "#fff", color: "#111", border: "1px solid #e5e7eb", borderBottomLeftRadius: "4px" }}
            >
              {m.text}
            </div>
          </div>
        ))}
        {sending && (
          <div className="flex justify-start">
            <div className="bg-white border border-gray-200 rounded-2xl px-4 py-2.5 text-gray-400 text-sm" style={{ borderBottomLeftRadius: "4px" }}>
              <span className="inline-flex gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: "0ms" }} />
                <span className="w-1.5 h-1.5 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: "150ms" }} />
                <span className="w-1.5 h-1.5 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: "300ms" }} />
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Lead capture form */}
      {leadFormVisible && !leadSubmitted && (
        <form onSubmit={submitLeadForm} className="border-t border-gray-100 p-3 flex flex-col gap-2 bg-white shrink-0">
          <p className="text-xs text-gray-500 font-medium">A few details to get started:</p>
          {cfg?.collectName && (
            <input
              data-testid="input-widget-name"
              placeholder="Your name"
              value={visitorName}
              onChange={e => setVisitorName(e.target.value)}
              className="border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-blue-400"
            />
          )}
          {cfg?.collectEmail && (
            <input
              data-testid="input-widget-email"
              type="email"
              placeholder="Email address"
              value={visitorEmail}
              onChange={e => setVisitorEmail(e.target.value)}
              required={cfg.collectEmail}
              className="border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-blue-400"
            />
          )}
          {cfg?.collectPhone && (
            <input
              data-testid="input-widget-phone"
              type="tel"
              placeholder="Phone (optional)"
              value={visitorPhone}
              onChange={e => setVisitorPhone(e.target.value)}
              className="border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-blue-400"
            />
          )}
          <button
            data-testid="button-widget-start"
            type="submit"
            className="py-2 rounded-lg text-sm font-semibold text-white"
            style={{ background: accent }}
          >
            Start Chat
          </button>
        </form>
      )}

      {/* Input row */}
      {(!leadFormVisible || leadSubmitted) && (
        <div className="border-t border-gray-100 flex items-center gap-2 px-3 py-2.5 bg-white shrink-0">
          <input
            data-testid="input-widget-message"
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
            placeholder="Type a message..."
            className="flex-1 border border-gray-200 rounded-xl px-3 py-2 text-sm outline-none focus:border-blue-400"
            disabled={sending}
          />
          <button
            data-testid="button-widget-send"
            onClick={sendMessage}
            disabled={!input.trim() || sending}
            className="w-8 h-8 rounded-xl flex items-center justify-center text-white shrink-0 disabled:opacity-40"
            style={{ background: accent }}
          >
            <Send size={14} />
          </button>
        </div>
      )}

      {/* Attribution / Powered by */}
      {renderAttribution()}
    </div>
  );
}
